const PASSWORD_SPECIAL_CHARS = [
    '!', '%', '*', '_', '+', '~', '-', '[', ']', '{', '}', '#', '=', '$', '@',
    '<', '>', ',', '.', ';', '?', "'", '"', '&', '(', ')', '^', '\\', '|', '/',
];

const PASSWORD_SPECIAL_LABEL = '!%*_+~-[]{}#=$@<>,.;?\'"&()^\\|/';

const PASSWORD_RULES = [
    {
        key: 'match',
        label: 'Both passwords need to match.',
    },
    {
        key: 'length',
        label: '8-50 characters.',
    },
    {
        key: 'number',
        label: 'At least one number.',
    },
    {
        key: 'capitalLetter',
        label: 'At least one capital letter.',
    },
    {
        key: 'lowercaseLetter',
        label: 'At least one lowercase letter.',
    },
    {
        key: 'personalInformation',
        label: 'Name, Birthday, Email, Work Email and Nationality are not allowed.',
    },
    {
        key: 'specialCharacter',
        label: `At least one of these special character: ${PASSWORD_SPECIAL_LABEL}`,
    },
];

const getPersonalInfoTokens = (personalInformation = {}) => {
    const tokens = [];
    const pushToken = (value) => {
        const normalized = String(value || '').trim();
        if (normalized.length > 1) tokens.push(normalized);
    };

    pushToken(personalInformation.firstName);
    pushToken(personalInformation.lastName);
    pushToken(personalInformation.email);
    pushToken(personalInformation.workEmail);
    pushToken(personalInformation.nationality);

    const fullName = [personalInformation.firstName, personalInformation.lastName]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
    pushToken(fullName);
    fullName.split(/\s+/).forEach(pushToken);

    return [...new Set(tokens)];
};

/**
 * Legacy DOB matcher from PasswordReset: optional 0-padding, optional /, -, ., or space
 * separators (or none), day/month either order, 4- or 2-digit year.
 * @param {Date|string|number} birthday
 * @param {string} password
 * @returns {boolean}
 */
const containsBirthday = (birthday, password = '') => {
    if (!birthday || !password) return false;

    const date = birthday instanceof Date ? birthday : new Date(birthday);
    if (Number.isNaN(date.getTime())) return false;

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return false;

    const twoDigitYear = String(year).substring(2, 4);
    const regex = new RegExp(
        `(0?(${day}|${month}))[\\/\\-\\.\\s]?(0?(${month}|${day}))[\\/\\-\\.\\s]?(${year}|${twoDigitYear})`,
    );

    return regex.test(String(password));
};

const containsPersonalInformation = (password = '', personalInformation = {}) => {
    const value = String(password || '').toLowerCase();
    if (!value) return false;

    if (containsBirthday(personalInformation.birthday, password)) return true;

    const tokens = getPersonalInfoTokens(personalInformation);
    return tokens.some((token) => {
        const normalized = String(token || '').trim().toLowerCase();
        return normalized.length > 1 && value.includes(normalized);
    });
};

const hasSpecialCharacter = (password = '') => (
    PASSWORD_SPECIAL_CHARS.some((char) => password.includes(char))
);

/**
 * Validates password against Automatrix rules (aligned with HR Login & Permissions).
 * @param {string} password
 * @param {string} confirmPassword
 * @param {object} personalInformation
 * @returns {{ valid: boolean, validation: object }}
 */
const validatePassword = (password = '', confirmPassword = '', personalInformation = {}) => {
    const value = String(password || '');
    const confirmValue = String(confirmPassword || '');

    if (!value) {
        return {
            valid: false,
            validation: {
                match: false,
                length: false,
                number: false,
                capitalLetter: false,
                lowercaseLetter: false,
                specialCharacter: false,
                personalInformation: false,
            },
        };
    }

    const validation = {
        match: value.length > 0 && value === confirmValue,
        length: value.length >= 8 && value.length <= 50,
        number: /\d/.test(value),
        capitalLetter: /[A-Z]/.test(value),
        lowercaseLetter: /[a-z]/.test(value),
        specialCharacter: hasSpecialCharacter(value),
        personalInformation: !containsPersonalInformation(value, personalInformation),
    };

    return {
        valid: Object.values(validation).every(Boolean),
        validation,
    };
};

export {
    PASSWORD_RULES,
    PASSWORD_SPECIAL_CHARS,
    PASSWORD_SPECIAL_LABEL,
    containsBirthday,
    getPersonalInfoTokens,
    validatePassword,
};
